/**
 * Fantasy Console 0 - Minimap Component
 *
 * Small overview of the entire map showing current viewport position.
 * Optimized: caches map image and only redraws viewport indicator on position changes.
 */

import {
  useRef,
  useEffect,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { MAP_WIDTH, MAP_HEIGHT } from '@fc0/types'
import { getSpriteDominantColor } from '../../utils/mapUtils'
import styles from './Minimap.module.css'
import editorStyles from '../../styles/editor.module.css'

interface MinimapProps {
  mapData: Uint8Array
  spriteData: Uint8Array
  viewportX: number
  viewportY: number
  viewportWidth: number
  viewportHeight: number
  onViewportClick: (x: number, y: number) => void
}

export function Minimap({
  mapData,
  spriteData,
  viewportX,
  viewportY,
  viewportWidth,
  viewportHeight,
  onViewportClick,
}: MinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Cached map image (only regenerated when map/sprites change)
  const cachedMapImageRef = useRef<ImageData | null>(null)

  // Cache for sprite colors
  const colorCacheRef = useRef<Map<number, [number, number, number]>>(new Map())

  // Clear color cache when sprites change
  useEffect(() => {
    colorCacheRef.current.clear()
    cachedMapImageRef.current = null
  }, [spriteData])

  // Regenerate map image only when map data changes
  useEffect(() => {
    const cache = colorCacheRef.current

    // Get or compute dominant color for a sprite
    const getDominantColor = (
      spriteIndex: number,
    ): [number, number, number] => {
      if (cache.has(spriteIndex)) {
        return cache.get(spriteIndex)!
      }
      const color = getSpriteDominantColor(spriteData, spriteIndex)
      cache.set(spriteIndex, color)
      return color
    }

    // Create image data for the entire map
    const imageData = new ImageData(MAP_WIDTH, MAP_HEIGHT)

    for (let y = 0; y < MAP_HEIGHT; y++) {
      for (let x = 0; x < MAP_WIDTH; x++) {
        const tileIndex = y * MAP_WIDTH + x
        const spriteIndex = mapData[tileIndex]
        const [r, g, b] = getDominantColor(spriteIndex)

        const pixelIndex = tileIndex * 4
        imageData.data[pixelIndex] = r
        imageData.data[pixelIndex + 1] = g
        imageData.data[pixelIndex + 2] = b
        imageData.data[pixelIndex + 3] = 255
      }
    }

    cachedMapImageRef.current = imageData
  }, [mapData, spriteData])

  // Render minimap (uses cached image, only draws viewport rect)
  useEffect(() => {
    const canvas = canvasRef.current
    const cachedImage = cachedMapImageRef.current
    if (!canvas || !cachedImage) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    canvas.width = MAP_WIDTH
    canvas.height = MAP_HEIGHT

    // Draw cached map image
    ctx.putImageData(cachedImage, 0, 0)

    // Draw viewport rectangle
    ctx.strokeStyle = '#ffec27'
    ctx.lineWidth = 1
    ctx.strokeRect(
      Math.floor(viewportX) + 0.5,
      Math.floor(viewportY) + 0.5,
      Math.ceil(viewportWidth) - 1,
      Math.ceil(viewportHeight) - 1,
    )
  }, [viewportX, viewportY, viewportWidth, viewportHeight, mapData, spriteData])

  // Refs for drag state
  const isDraggingRef = useRef(false)
  const onViewportClickRef = useRef(onViewportClick)
  const viewportWidthRef = useRef(viewportWidth)
  const viewportHeightRef = useRef(viewportHeight)
  onViewportClickRef.current = onViewportClick
  viewportWidthRef.current = viewportWidth
  viewportHeightRef.current = viewportHeight

  const computeViewportPosition = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const scaleX = MAP_WIDTH / rect.width
      const scaleY = MAP_HEIGHT / rect.height

      const x = (clientX - rect.left) * scaleX
      const y = (clientY - rect.top) * scaleY

      const vw = viewportWidthRef.current
      const vh = viewportHeightRef.current
      const newX = Math.max(0, Math.min(MAP_WIDTH - vw, x - vw / 2))
      const newY = Math.max(0, Math.min(MAP_HEIGHT - vh, y - vh / 2))

      onViewportClickRef.current(newX, newY)
    },
    [],
  )

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      isDraggingRef.current = true
      computeViewportPosition(e.clientX, e.clientY)

      const handleMouseMove = (ev: globalThis.MouseEvent) => {
        if (!isDraggingRef.current) return
        computeViewportPosition(ev.clientX, ev.clientY)
      }

      const handleMouseUp = () => {
        isDraggingRef.current = false
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [computeViewportPosition],
  )

  // Clean up drag listeners on unmount
  useEffect(() => {
    return () => {
      isDraggingRef.current = false
    }
  }, [])

  return (
    <div className={editorStyles.panelContainer}>
      <div className={styles.header}>
        <span className={editorStyles.panelLabel}>Minimap</span>
      </div>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        onMouseDown={handleMouseDown}
        title='Click or drag to navigate'
        aria-label='Map overview'
      />
    </div>
  )
}
